import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Map, Server, Globe, Database, Monitor, Wifi, Router, Shield, AlertTriangle,
  ChevronRight, Cloud, HardDrive, Network, Layers, Target, Loader2,
  Factory, Cpu, Smartphone, ZoomIn, ZoomOut, Move, Eye, X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Asset type configurations with network topology hierarchy
const ASSET_TYPES = {
  internet: { icon: Globe, color: '#3B82F6', label: 'Internet/Cloud', level: 0 },
  firewall: { icon: Shield, color: '#EF4444', label: 'Firewall', level: 1 },
  router: { icon: Router, color: '#8B5CF6', label: 'Switch/Core', level: 2 },
  switch: { icon: Network, color: '#8B5CF6', label: 'Switch', level: 3 },
  vlan: { icon: Layers, color: '#06B6D4', label: 'VLANs', level: 4 },
  server: { icon: Server, color: '#10B981', label: 'Serveur', level: 5 },
  web: { icon: Globe, color: '#10B981', label: 'Web', level: 5 },
  database: { icon: Database, color: '#10B981', label: 'Base de données', level: 5 },
  workstation: { icon: Monitor, color: '#10B981', label: 'Poste de travail', level: 5 },
  cloud: { icon: Cloud, color: '#3B82F6', label: 'Cloud', level: 5 },
  iot: { icon: Wifi, color: '#10B981', label: 'IoT', level: 5 },
  ot: { icon: Factory, color: '#F59E0B', label: 'OT/SCADA', level: 5 },
  plc: { icon: Cpu, color: '#84CC16', label: 'PLC/RTU', level: 5 },
  mobile: { icon: Smartphone, color: '#EC4899', label: 'Mobile', level: 5 },
  unknown: { icon: HardDrive, color: '#6B7280', label: 'Périphérique', level: 5 }
};

// Risk colors based on vulnerability severity
const RISK_COLORS = {
  critical: '#9333EA',  // Purple for critical
  high: '#EF4444',      // Red for high
  medium: '#F59E0B',    // Orange for medium
  low: '#EAB308',       // Yellow for low
  clean: '#10B981'      // Green for no vulnerabilities
};

// Function to determine risk color based on vulnerabilities
const getRiskColor = (node) => {
  // Infrastructure nodes (levels 0-4) keep their type color
  const typeConfig = ASSET_TYPES[node.type] || ASSET_TYPES.unknown;
  if (typeConfig.level < 5) {
    return typeConfig.color;
  }

  // For assets (level 5), determine color by risk
  const vulnerabilities = node.vulnerabilities || [];
  const vulnCount = node.vulnerabilities_count || vulnerabilities.length || 0;

  if (vulnCount === 0) {
    return RISK_COLORS.clean;
  }

  // Check for critical vulnerabilities
  const criticalCount = vulnerabilities.filter(v =>
    v.severity?.toLowerCase() === 'critical' || v.cvss_score >= 9.0
  ).length;
  if (criticalCount > 0) {
    return RISK_COLORS.critical;
  }

  // Check for high vulnerabilities
  const highCount = vulnerabilities.filter(v =>
    v.severity?.toLowerCase() === 'high' || (v.cvss_score >= 7.0 && v.cvss_score < 9.0)
  ).length;
  if (highCount > 0) {
    return RISK_COLORS.high;
  }

  // Check for medium vulnerabilities
  const mediumCount = vulnerabilities.filter(v =>
    v.severity?.toLowerCase() === 'medium' || (v.cvss_score >= 4.0 && v.cvss_score < 7.0)
  ).length;
  if (mediumCount > 0) {
    return RISK_COLORS.medium;
  }

  // Low or unknown severity
  return RISK_COLORS.low;
};

// Legend items for the bottom bar - Infrastructure types
const LEGEND_ITEMS = [
  { color: '#3B82F6', label: 'Internet/Cloud', icon: Globe },
  { color: '#EF4444', label: 'Firewall', icon: Shield },
  { color: '#8B5CF6', label: 'Switch/Core', icon: Network },
  { color: '#06B6D4', label: 'VLANs', icon: Layers }
];

// Legend items for risk levels
const RISK_LEGEND_ITEMS = [
  { color: RISK_COLORS.critical, label: 'Critique' },
  { color: RISK_COLORS.high, label: 'Élevé' },
  { color: RISK_COLORS.medium, label: 'Moyen' },
  { color: RISK_COLORS.low, label: 'Faible' },
  { color: RISK_COLORS.clean, label: 'Sain' }
];

// Normalize host data from API
const normalizeHost = (host) => {
  return {
    ...host,
    ip: host.ip_address || host.ip,
    hostname: host.hostname || host.ip_address || host.ip,
    ports: host.ports || (host.open_ports ? Array.from({length: host.open_ports}, (_, i) => i + 1) : []),
    services: host.services || [],
    os: host.os_name || host.os || null,
    vulnerabilities: host.vulnerabilities || [],
    vulnerabilities_count: host.vulnerabilities_count || 0,
    status: host.status || 'up'
  };
};

// Detect asset type from host data
const detectAssetType = (host) => {
  const services = (host.services || []).map(s => s.toLowerCase());
  const ports = host.ports || [];
  const os = (host.os || host.os_name || '').toLowerCase();
  const hostname = (host.hostname || host.ip_address || host.ip || '').toLowerCase();

  // Firewall detection
  if (os.includes('firewall') || hostname.includes('fw') || hostname.includes('firewall') ||
      hostname.includes('pfsense') || hostname.includes('fortigate') || hostname.includes('palo')) {
    return 'firewall';
  }

  // Router/Switch detection
  if (os.includes('router') || os.includes('cisco') || hostname.includes('router') ||
      hostname.includes('gw') || hostname.includes('gateway') || hostname.includes('switch') ||
      hostname.includes('core')) {
    return 'router';
  }

  // Web server detection
  if (services.some(s => s.includes('http') || s.includes('nginx') || s.includes('apache'))) {
    if (ports.includes(443) || ports.includes(80) || ports.includes(8080)) return 'web';
  }

  // Database detection
  if (services.some(s => s.includes('mysql') || s.includes('postgres') || s.includes('mongo') ||
      s.includes('oracle') || s.includes('mssql') || s.includes('redis'))) {
    return 'database';
  }

  // OT/SCADA detection
  if (services.some(s => s.includes('modbus') || s.includes('s7') || s.includes('bacnet') ||
      s.includes('dnp3') || s.includes('enip'))) {
    return 'ot';
  }

  // PLC detection
  if (hostname.includes('plc') || hostname.includes('rtu') || hostname.includes('scada')) {
    return 'plc';
  }

  // IoT detection
  if (services.some(s => s.includes('mqtt') || s.includes('coap') || s.includes('zigbee')) ||
      hostname.includes('iot') || hostname.includes('sensor')) {
    return 'iot';
  }

  // Workstation detection
  if (os.includes('windows') && (hostname.includes('pc') || hostname.includes('desktop') ||
      hostname.includes('laptop') || hostname.includes('workstation'))) {
    return 'workstation';
  }

  // Server detection
  if (os.includes('server') || os.includes('linux') || services.some(s =>
      s.includes('ssh') || s.includes('rdp') || s.includes('smb'))) {
    return 'server';
  }

  // Cloud detection
  if (hostname.includes('aws') || hostname.includes('azure') || hostname.includes('gcp') ||
      hostname.includes('cloud') || hostname.includes('ec2')) {
    return 'cloud';
  }

  return 'unknown';
};

// Build network topology tree from hosts
const buildNetworkTopology = (hosts) => {
  const nodes = [];
  const edges = [];

  // Create Internet root node
  const internetNode = {
    id: 'internet',
    type: 'internet',
    label: 'Internet',
    x: 0,
    y: 0,
    children: []
  };
  nodes.push(internetNode);

  // Create Firewall node
  const firewallNode = {
    id: 'firewall-principal',
    type: 'firewall',
    label: 'Firewall Principal',
    x: 0,
    y: 1,
    parentId: 'internet',
    children: [],
    bandwidth: '1 Gbps WAN'
  };
  nodes.push(firewallNode);
  edges.push({ from: 'internet', to: 'firewall-principal' });

  // Create Core Switch
  const coreSwitch = {
    id: 'core-switch',
    type: 'router',
    label: 'Core Switch',
    x: 0,
    y: 2,
    parentId: 'firewall-principal',
    children: [],
    bandwidth: '10 Gbps'
  };
  nodes.push(coreSwitch);
  edges.push({ from: 'firewall-principal', to: 'core-switch' });

  // Group hosts by subnet/network
  const subnets = {};
  hosts.forEach(host => {
    const ip = host.ip || '';
    const parts = ip.split('.');
    if (parts.length >= 3) {
      const subnet = `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
      if (!subnets[subnet]) {
        subnets[subnet] = [];
      }
      subnets[subnet].push(host);
    }
  });

  // Create switch and VLAN nodes for each subnet
  const subnetKeys = Object.keys(subnets);
  const switchTypes = ['Site 1 (Siège)', 'DMZ', 'Site 2 (Agence)'];
  const vlanNames = ['Admin', 'Prod', 'Bureau'];

  subnetKeys.forEach((subnet, idx) => {
    const switchIdx = idx % 3;
    const switchId = `switch-${idx}`;
    const vlanId = `vlan-${idx}`;

    // Create Switch node
    const switchNode = {
      id: switchId,
      type: 'switch',
      label: `Switch ${switchTypes[switchIdx] || `Site ${idx + 1}`}`,
      x: idx - (subnetKeys.length - 1) / 2,
      y: 3,
      parentId: 'core-switch',
      children: [],
      bandwidth: '1 Gbps'
    };
    nodes.push(switchNode);
    edges.push({ from: 'core-switch', to: switchId });

    // Create VLAN node
    const vlanNumber = 10 + (idx * 10);
    const vlanNode = {
      id: vlanId,
      type: 'vlan',
      label: `VLAN ${vlanNumber} - ${vlanNames[switchIdx] || subnet}`,
      x: idx - (subnetKeys.length - 1) / 2,
      y: 4,
      parentId: switchId,
      children: [],
      subnet: subnet
    };
    nodes.push(vlanNode);
    edges.push({ from: switchId, to: vlanId });

    // Add hosts under the VLAN
    subnets[subnet].forEach((host, hostIdx) => {
      const hostType = host.type || detectAssetType(host);

      const hostNode = {
        id: host.id || `host-${idx}-${hostIdx}`,
        type: hostType,
        label: host.hostname || host.ip,
        ip: host.ip,
        x: (idx - (subnetKeys.length - 1) / 2) + (hostIdx - (subnets[subnet].length - 1) / 2) * 0.4,
        y: 5,
        parentId: vlanId,
        data: host,
        ports: host.ports || [],
        openPorts: host.open_ports || (host.ports ? host.ports.length : 0),
        services: host.services || [],
        os: host.os,
        vulnerabilities: host.vulnerabilities || [],
        vulnerabilities_count: host.vulnerabilities_count || 0,
        status: host.status || 'up'
      };
      nodes.push(hostNode);
      edges.push({ from: vlanId, to: hostNode.id });
    });
  });

  // If no hosts, create sample structure
  if (hosts.length === 0) {
    // Add sample switches
    ['Site 1 (Siège)', 'DMZ', 'Site 2 (Agence)'].forEach((name, idx) => {
      const switchId = `switch-sample-${idx}`;
      nodes.push({
        id: switchId,
        type: 'switch',
        label: `Switch ${name}`,
        x: idx - 1,
        y: 3,
        parentId: 'core-switch',
        children: []
      });
      edges.push({ from: 'core-switch', to: switchId });
    });
  }

  return { nodes, edges };
};

// Floating particles component
const FloatingParticles = ({ count = 30 }) => {
  const particles = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 10,
      opacity: Math.random() * 0.3 + 0.1
    }));
  }, [count]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full bg-purple-500"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            opacity: p.opacity,
            animation: `floatParticle ${p.duration}s ease-in-out infinite`,
            animationDelay: `${p.delay}s`
          }}
        />
      ))}
      <style>{`
        @keyframes floatParticle {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.1; }
          25% { transform: translate(20px, -30px) scale(1.2); opacity: 0.3; }
          50% { transform: translate(-10px, -50px) scale(1); opacity: 0.2; }
          75% { transform: translate(30px, -20px) scale(0.8); opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

// Get risk label from node
const getRiskLabel = (node) => {
  const vulnerabilities = node.vulnerabilities || [];
  const vulnCount = node.vulnerabilities_count || vulnerabilities.length || 0;

  if (vulnCount === 0) return { label: 'Sain', color: RISK_COLORS.clean };

  const criticalCount = vulnerabilities.filter(v =>
    v.severity?.toLowerCase() === 'critical' || v.cvss_score >= 9.0
  ).length;
  if (criticalCount > 0) return { label: 'Critique', color: RISK_COLORS.critical };

  const highCount = vulnerabilities.filter(v =>
    v.severity?.toLowerCase() === 'high' || (v.cvss_score >= 7.0 && v.cvss_score < 9.0)
  ).length;
  if (highCount > 0) return { label: 'Élevé', color: RISK_COLORS.high };

  const mediumCount = vulnerabilities.filter(v =>
    v.severity?.toLowerCase() === 'medium' || (v.cvss_score >= 4.0 && v.cvss_score < 7.0)
  ).length;
  if (mediumCount > 0) return { label: 'Moyen', color: RISK_COLORS.medium };

  return { label: 'Faible', color: RISK_COLORS.low };
};

// Hover tooltip component
const NodeTooltip = ({ node, position }) => {
  if (!node) return null;

  const typeConfig = ASSET_TYPES[node.type] || ASSET_TYPES.unknown;
  const IconComponent = typeConfig.icon;
  const nodeColor = getRiskColor(node);
  const portsCount = node.openPorts || node.ports?.length || 0;
  const vulnCount = node.vulnerabilities_count || node.vulnerabilities?.length || 0;
  const riskInfo = typeConfig.level >= 5 ? getRiskLabel(node) : null;

  return (
    <div
      className="fixed z-50 bg-gray-900/95 backdrop-blur-md rounded-lg border border-gray-700 shadow-2xl p-3 min-w-[200px] max-w-[280px] pointer-events-none"
      style={{
        left: position.x + 15,
        top: position.y - 10,
        transform: 'translateY(-50%)'
      }}
    >
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-700">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${nodeColor}20` }}
        >
          <IconComponent size={16} color={nodeColor} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate">{node.label}</div>
          {node.ip && <div className="text-gray-400 text-xs">{node.ip}</div>}
        </div>
      </div>

      <div className="space-y-1.5 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-gray-400">Type</span>
          <span
            className="px-2 py-0.5 rounded"
            style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
          >
            {typeConfig.label}
          </span>
        </div>

        {riskInfo && (
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Niveau de risque</span>
            <span
              className="px-2 py-0.5 rounded font-medium flex items-center gap-1.5"
              style={{ backgroundColor: `${riskInfo.color}20`, color: riskInfo.color }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: riskInfo.color }}
              />
              {riskInfo.label}
            </span>
          </div>
        )}

        {node.os && (
          <div className="flex items-center justify-between">
            <span className="text-gray-400">OS</span>
            <span className="text-white truncate ml-2 max-w-[140px]">{node.os}</span>
          </div>
        )}

        {portsCount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Ports ouverts</span>
            <span className="text-cyan-400 font-medium">{portsCount}</span>
          </div>
        )}

        {vulnCount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Vulnérabilités</span>
            <span className="text-red-400 font-medium">{vulnCount}</span>
          </div>
        )}

        {node.bandwidth && (
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Bande passante</span>
            <span className="text-green-400">{node.bandwidth}</span>
          </div>
        )}

        {node.status && (
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Status</span>
            <span className={`flex items-center gap-1 ${node.status === 'up' ? 'text-green-400' : 'text-red-400'}`}>
              <span className={`w-2 h-2 rounded-full ${node.status === 'up' ? 'bg-green-400' : 'bg-red-400'}`}></span>
              {node.status === 'up' ? 'En ligne' : 'Hors ligne'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Network Node Component
const NetworkNode = ({ node, scale, onSelect, isSelected, onHover, onHoverEnd }) => {
  const typeConfig = ASSET_TYPES[node.type] || ASSET_TYPES.unknown;
  const IconComponent = typeConfig.icon;

  // Use risk-based color for assets, type color for infrastructure
  const nodeColor = getRiskColor(node);

  const nodeSize = node.type === 'internet' || node.type === 'firewall' || node.type === 'router' ? 56 : 48;
  const iconSize = node.type === 'internet' || node.type === 'firewall' || node.type === 'router' ? 24 : 20;

  const vulnCount = node.vulnerabilities_count || node.vulnerabilities?.length || 0;
  const criticalCount = node.vulnerabilities?.filter(v => v.severity === 'critical').length || 0;
  const portsCount = node.openPorts || node.ports?.length || 0;

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect?.(node);
  };

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    onHover?.(node, { x: rect.right, y: rect.top + rect.height / 2 });
  };

  const handleMouseLeave = () => {
    onHoverEnd?.();
  };

  return (
    <g
      transform={`translate(${node.x * 180}, ${node.y * 120})`}
      className="cursor-pointer"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ transformOrigin: 'center' }}
    >
      {/* Glow effect */}
      <defs>
        <filter id={`glow-${node.id}`} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="6" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id={`gradient-${node.id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={nodeColor} stopOpacity="0.4" />
          <stop offset="100%" stopColor={nodeColor} stopOpacity="0.05" />
        </radialGradient>
      </defs>

      {/* Animated pulse ring for selected */}
      {isSelected && (
        <circle
          r={nodeSize * 0.9}
          fill="none"
          stroke={nodeColor}
          strokeWidth="2"
          opacity="0.5"
        >
          <animate attributeName="r" from={nodeSize * 0.6} to={nodeSize * 1.2} dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.6" to="0" dur="1.5s" repeatCount="indefinite" />
        </circle>
      )}

      {/* Background glow circle */}
      <circle
        r={nodeSize * 0.8}
        fill={`url(#gradient-${node.id})`}
        filter={isSelected ? `url(#glow-${node.id})` : undefined}
        className="transition-all duration-300"
      />

      {/* Main node circle */}
      <circle
        r={nodeSize / 2}
        fill="#1F2937"
        stroke={nodeColor}
        strokeWidth={isSelected ? 3 : 2}
        className="transition-all duration-200"
      />

      {/* Inner highlight */}
      <circle
        r={nodeSize / 2 - 3}
        fill="none"
        stroke={nodeColor}
        strokeWidth="0.5"
        opacity="0.3"
      />

      {/* Icon background */}
      <circle
        r={iconSize * 0.8}
        fill={`${nodeColor}15`}
      />

      {/* Icon */}
      <foreignObject
        x={-iconSize / 2}
        y={-iconSize / 2}
        width={iconSize}
        height={iconSize}
        style={{ pointerEvents: 'none' }}
      >
        <div className="w-full h-full flex items-center justify-center">
          <IconComponent
            size={iconSize}
            color={nodeColor}
            strokeWidth={1.5}
          />
        </div>
      </foreignObject>

      {/* Label */}
      <text
        y={nodeSize / 2 + 16}
        textAnchor="middle"
        fill="white"
        fontSize={11}
        fontWeight="500"
        className="pointer-events-none select-none"
      >
        {node.label?.length > 18 ? node.label.substring(0, 18) + '...' : node.label}
      </text>

      {/* IP address for hosts */}
      {node.ip && (
        <text
          y={nodeSize / 2 + 30}
          textAnchor="middle"
          fill="#9CA3AF"
          fontSize={9}
          className="pointer-events-none select-none"
        >
          {node.ip}
        </text>
      )}

      {/* Ports count badge */}
      {portsCount > 0 && (
        <g transform={`translate(${-nodeSize / 2 + 4}, ${-nodeSize / 2 + 4})`}>
          <circle r={9} fill="#06B6D4" />
          <text
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize={8}
            fontWeight="bold"
            className="pointer-events-none"
          >
            {portsCount}
          </text>
        </g>
      )}

      {/* Vulnerability badge */}
      {vulnCount > 0 && (
        <g transform={`translate(${nodeSize / 2 - 4}, ${-nodeSize / 2 + 4})`}>
          <circle r={10} fill={criticalCount > 0 ? '#9333EA' : '#EF4444'} />
          <text
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize={9}
            fontWeight="bold"
            className="pointer-events-none"
          >
            {vulnCount}
          </text>
        </g>
      )}

      {/* Bandwidth label */}
      {node.bandwidth && (
        <text
          y={-nodeSize / 2 - 8}
          textAnchor="middle"
          fill="#6B7280"
          fontSize={8}
          className="pointer-events-none"
        >
          {node.bandwidth}
        </text>
      )}
    </g>
  );
};

// Connection Line Component with animated particles
const ConnectionLine = ({ from, to, nodes, showParticles = true }) => {
  const fromNode = nodes.find(n => n.id === from);
  const toNode = nodes.find(n => n.id === to);

  if (!fromNode || !toNode) return null;

  const x1 = fromNode.x * 180;
  const y1 = fromNode.y * 120 + 30;
  const x2 = toNode.x * 180;
  const y2 = toNode.y * 120 - 30;

  // Calculate control points for curved line
  const midY = (y1 + y2) / 2;
  const pathId = `path-${from}-${to}`;

  return (
    <g>
      <defs>
        <linearGradient id={`line-gradient-${from}-${to}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#6366F1" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.3" />
        </linearGradient>
      </defs>

      {/* Main path */}
      <path
        id={pathId}
        d={`M ${x1} ${y1} Q ${x1} ${midY} ${(x1 + x2) / 2} ${midY} Q ${x2} ${midY} ${x2} ${y2}`}
        fill="none"
        stroke={`url(#line-gradient-${from}-${to})`}
        strokeWidth={2}
        className="opacity-70"
      />

      {/* Animated particle along the path */}
      {showParticles && (
        <circle r={3} fill="#A855F7">
          <animateMotion dur="3s" repeatCount="indefinite">
            <mpath href={`#${pathId}`} />
          </animateMotion>
          <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
        </circle>
      )}

      {/* Connection dot */}
      <circle
        cx={(x1 + x2) / 2}
        cy={midY}
        r={3}
        fill="#6366F1"
        className="opacity-60"
      />
    </g>
  );
};

// Asset Details Panel
const AssetDetailsPanel = ({ node, onClose }) => {
  const { t } = useTranslation();

  if (!node) return null;

  const typeConfig = ASSET_TYPES[node.type] || ASSET_TYPES.unknown;
  const IconComponent = typeConfig.icon;
  const hostData = node.data || node;
  const portsCount = node.openPorts || node.ports?.length || 0;

  return (
    <div className="absolute top-4 right-16 w-80 bg-gray-900/95 backdrop-blur-md rounded-xl border border-gray-700 shadow-2xl z-20 overflow-hidden">
      <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${typeConfig.color}20`, border: `2px solid ${typeConfig.color}50` }}
            >
              <IconComponent size={24} color={typeConfig.color} />
            </div>
            <div>
              <h4 className="text-white font-bold text-sm">{node.label}</h4>
              <p className="text-gray-400 text-xs">{node.ip || typeConfig.label}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
        {/* Status indicator */}
        {node.status && (
          <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
            <span className="text-gray-400 text-sm">Status</span>
            <div className={`flex items-center gap-2 ${node.status === 'up' ? 'text-green-400' : 'text-red-400'}`}>
              <span className={`w-2 h-2 rounded-full animate-pulse ${node.status === 'up' ? 'bg-green-400' : 'bg-red-400'}`}></span>
              <span className="text-sm font-medium">{node.status === 'up' ? 'En ligne' : 'Hors ligne'}</span>
            </div>
          </div>
        )}

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-3">
          {node.os && (
            <div className="col-span-2 p-3 bg-gray-800/50 rounded-lg">
              <span className="text-gray-400 text-xs block mb-1">Système d'exploitation</span>
              <span className="text-white text-sm">{node.os}</span>
            </div>
          )}

          {portsCount > 0 && (
            <div className="p-3 bg-gray-800/50 rounded-lg">
              <span className="text-gray-400 text-xs block mb-1">Ports ouverts</span>
              <span className="text-cyan-400 text-lg font-bold">{portsCount}</span>
            </div>
          )}

          {(node.vulnerabilities_count > 0 || node.vulnerabilities?.length > 0) && (
            <div className="p-3 bg-gray-800/50 rounded-lg">
              <span className="text-gray-400 text-xs block mb-1">Vulnérabilités</span>
              <span className="text-red-400 text-lg font-bold">
                {node.vulnerabilities_count || node.vulnerabilities?.length || 0}
              </span>
            </div>
          )}
        </div>

        {/* Type badge */}
        <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
          <span className="text-gray-400 text-sm">Type d'asset</span>
          <span
            className="px-3 py-1 rounded-full text-xs font-medium"
            style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
          >
            {typeConfig.label}
          </span>
        </div>

        {/* Ports list */}
        {node.ports?.length > 0 && (
          <div className="p-3 bg-gray-800/50 rounded-lg">
            <span className="text-gray-400 text-xs block mb-2">{t('common.openPorts', 'Ports ouverts')}</span>
            <div className="flex flex-wrap gap-1.5">
              {node.ports.slice(0, 15).map((port, idx) => (
                <span key={idx} className="px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded text-xs font-mono">
                  {port}
                </span>
              ))}
              {node.ports.length > 15 && (
                <span className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs">
                  +{node.ports.length - 15}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Services */}
        {node.services?.length > 0 && (
          <div className="p-3 bg-gray-800/50 rounded-lg">
            <span className="text-gray-400 text-xs block mb-2">Services détectés</span>
            <div className="flex flex-wrap gap-1.5">
              {node.services.slice(0, 8).map((svc, idx) => (
                <span key={idx} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                  {svc}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Vulnerabilities */}
        {node.vulnerabilities?.length > 0 && (
          <div className="p-3 bg-gray-800/50 rounded-lg">
            <span className="text-gray-400 text-xs block mb-2">
              {t('common.vulnerabilities', 'Vulnérabilités')}
            </span>
            <div className="space-y-1.5">
              {node.vulnerabilities.slice(0, 5).map((vuln, idx) => (
                <div
                  key={idx}
                  className={`px-2 py-1.5 rounded text-xs ${
                    vuln.severity === 'critical' ? 'bg-purple-500/20 text-purple-300 border-l-2 border-purple-500' :
                    vuln.severity === 'high' ? 'bg-red-500/20 text-red-300 border-l-2 border-red-500' :
                    vuln.severity === 'medium' ? 'bg-orange-500/20 text-orange-300 border-l-2 border-orange-500' :
                    'bg-yellow-500/20 text-yellow-300 border-l-2 border-yellow-500'
                  }`}
                >
                  {vuln.cve_id || vuln.title?.substring(0, 30) || 'Vulnérabilité'}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bandwidth info for infrastructure nodes */}
        {node.bandwidth && (
          <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
            <span className="text-gray-400 text-sm">Bande passante</span>
            <span className="text-green-400 font-medium">{node.bandwidth}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const AssetCartography = ({ scans = [], selectedScanId = null }) => {
  const { t } = useTranslation();
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [currentScan, setCurrentScan] = useState(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  // Get the most recent completed scan
  const targetScan = useMemo(() => {
    if (selectedScanId) {
      return scans.find(s => s.id === selectedScanId);
    }
    const completedScans = scans.filter(s => s.status === 'completed');
    if (completedScans.length > 0) {
      return completedScans.sort((a, b) =>
        new Date(b.completed_at || b.created_at) - new Date(a.completed_at || a.created_at)
      )[0];
    }
    return scans[0];
  }, [scans, selectedScanId]);

  // Fetch hosts
  useEffect(() => {
    const fetchHosts = async () => {
      if (!targetScan?.id) {
        setHosts([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/v1/scan/${targetScan.id}/hosts`, {
          headers: getAuthHeaders()
        });

        if (response.ok) {
          const data = await response.json();
          // Normalize hosts from API and detect asset types
          const processedHosts = (data.hosts || data || []).map(host => {
            const normalized = normalizeHost(host);
            return {
              ...normalized,
              type: detectAssetType(normalized)
            };
          });
          setHosts(processedHosts);
          setCurrentScan(targetScan);
        }
      } catch (error) {
        console.error('Failed to fetch hosts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHosts();
  }, [targetScan?.id]);

  // Build topology
  const topology = useMemo(() => buildNetworkTopology(hosts), [hosts]);

  // Calculate SVG viewBox
  const viewBox = useMemo(() => {
    if (topology.nodes.length === 0) return '-400 -50 800 800';

    const xs = topology.nodes.map(n => n.x * 180);
    const ys = topology.nodes.map(n => n.y * 120);
    const minX = Math.min(...xs) - 150;
    const maxX = Math.max(...xs) + 150;
    const minY = Math.min(...ys) - 80;
    const maxY = Math.max(...ys) + 120;

    return `${minX} ${minY} ${maxX - minX} ${maxY - minY}`;
  }, [topology.nodes]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setScale(s => Math.min(s * 1.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(s => Math.max(s / 1.2, 0.3));
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Hover handlers
  const handleNodeHover = useCallback((node, pos) => {
    setHoveredNode(node);
    setTooltipPosition(pos);
  }, []);

  const handleNodeHoverEnd = useCallback(() => {
    setHoveredNode(null);
  }, []);

  // Pan handlers
  const handleMouseDown = useCallback((e) => {
    // Only start dragging if clicking on the SVG background
    if (e.target.tagName === 'svg' || e.target.tagName === 'rect') {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(s => Math.min(Math.max(s * delta, 0.3), 3));
  }, []);

  if (!targetScan) {
    return (
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-8 text-center h-[700px] flex flex-col items-center justify-center">
        <Map className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">
          {t('scan.noScanAvailable', 'Aucun scan disponible')}
        </h3>
        <p className="text-gray-500">
          {t('scan.launchScanForCartography', 'Lancez un scan pour visualiser la cartographie des assets.')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#0D1117] rounded-xl border border-gray-800 overflow-hidden relative">
      {/* Floating particles background */}
      <FloatingParticles count={40} />

      {/* Header */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-900/90 backdrop-blur-sm rounded-lg border border-gray-700">
          <Map className="w-4 h-4 text-purple-400" />
          <span className="text-white font-medium text-sm">
            {t('scan.networkCartography', 'Cartographie Réseau')} - Multi-Sites
          </span>
        </div>
        {currentScan && (
          <div className="px-3 py-2 bg-gray-900/90 backdrop-blur-sm rounded-lg border border-gray-700">
            <span className="text-gray-400 text-xs">
              {hosts.length} {t('common.assets', 'assets')}
            </span>
          </div>
        )}
        {loading && (
          <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
        )}
      </div>

      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        <button
          onClick={handleZoomIn}
          className="w-9 h-9 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center justify-center text-white transition-colors shadow-lg"
          title="Zoom in"
        >
          <ZoomIn size={18} />
        </button>
        <button
          onClick={handleZoomOut}
          className="w-9 h-9 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center justify-center text-white transition-colors shadow-lg"
          title="Zoom out"
        >
          <ZoomOut size={18} />
        </button>
        <button
          onClick={handleReset}
          className="w-9 h-9 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center text-white transition-colors shadow-lg"
          title="Reset view"
        >
          <Move size={18} />
        </button>
      </div>

      {/* SVG Canvas - Increased height */}
      <div
        ref={containerRef}
        className="w-full h-[700px] overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox={viewBox}
          className="select-none"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transformOrigin: 'center center'
          }}
        >
          {/* Background */}
          <rect x="-2000" y="-1000" width="4000" height="3000" fill="#0D1117" />

          {/* Grid pattern */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1F2937" strokeWidth="0.5" opacity="0.3" />
            </pattern>
            <pattern id="gridLarge" width="200" height="200" patternUnits="userSpaceOnUse">
              <path d="M 200 0 L 0 0 0 200" fill="none" stroke="#374151" strokeWidth="0.3" opacity="0.2" />
            </pattern>
          </defs>
          <rect x="-2000" y="-1000" width="4000" height="3000" fill="url(#grid)" />
          <rect x="-2000" y="-1000" width="4000" height="3000" fill="url(#gridLarge)" />

          {/* Connection Lines */}
          <g className="connections">
            {topology.edges.map((edge, idx) => (
              <ConnectionLine
                key={idx}
                from={edge.from}
                to={edge.to}
                nodes={topology.nodes}
                showParticles={idx < 10} // Limit animated particles for performance
              />
            ))}
          </g>

          {/* Nodes */}
          <g className="nodes">
            {topology.nodes.map(node => (
              <NetworkNode
                key={node.id}
                node={node}
                scale={scale}
                onSelect={setSelectedNode}
                isSelected={selectedNode?.id === node.id}
                onHover={handleNodeHover}
                onHoverEnd={handleNodeHoverEnd}
              />
            ))}
          </g>
        </svg>
      </div>

      {/* Legend */}
      <div className="absolute bottom-0 left-0 right-0 px-6 py-3 bg-gradient-to-t from-[#0D1117] via-[#0D1117]/95 to-transparent">
        <div className="flex items-center justify-center gap-8 flex-wrap">
          {/* Infrastructure Legend */}
          <div className="flex items-center gap-4">
            <span className="text-gray-500 text-xs font-medium mr-1">Infrastructure:</span>
            {LEGEND_ITEMS.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <div key={idx} className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <IconComponent size={12} color={item.color} />
                  <span className="text-gray-400 text-xs">{item.label}</span>
                </div>
              );
            })}
          </div>

          {/* Risk Level Legend */}
          <div className="flex items-center gap-4 border-l border-gray-700 pl-6">
            <span className="text-gray-500 text-xs font-medium mr-1">Niveau de risque:</span>
            {RISK_LEGEND_ITEMS.map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-400 text-xs">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hover Tooltip */}
      {hoveredNode && !selectedNode && (
        <NodeTooltip node={hoveredNode} position={tooltipPosition} />
      )}

      {/* Selected Node Details Panel */}
      {selectedNode && (
        <AssetDetailsPanel
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </div>
  );
};

export default AssetCartography;
